import { useEffect, useRef } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import { AssetClass } from "@/types/portfolio";
import { ASSET_CLASS_COLORS } from "@/constants/investmentColors";

interface AssetAllocationChartProps {
  assetClasses: AssetClass[];
}

const AssetAllocationChart = ({ assetClasses }: AssetAllocationChartProps) => {
  const chartRef = useRef<am5.Root | null>(null);
  const seriesRef = useRef<am5xy.ColumnSeries | null>(null);

  useEffect(() => {
    if (!chartRef.current) {
      const root = am5.Root.new("chartdiv");
      chartRef.current = root;

      root.setThemes([am5.Theme.new(root)]);

      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: false,
          panY: false,
          wheelX: "none",
          wheelY: "none",
          layout: root.verticalLayout,
          paddingLeft: 20,
          paddingRight: 20
        })
      );

      const yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: am5xy.AxisRendererY.new(root, {}),
          min: 0,
          max: 100,
          numberFormat: "#'%'"
        })
      );

      const xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: "category",
          renderer: am5xy.AxisRendererX.new(root, {
            minGridDistance: 30
          })
        })
      );

      const tooltip = am5.Tooltip.new(root, {
        labelText: "{name}: {valueY}%",
        pointerOrientation: "horizontal",
        animationDuration: 200
      });

      tooltip.get("background")?.setAll({
        fill: am5.color("#1a2b4c"),
        fillOpacity: 0.9,
        stroke: am5.color("#ffffff"),
        strokeOpacity: 0.2
      });

      const data = [{
        category: "Portfolio",
        ...assetClasses.reduce((acc, asset) => {
          // Ensure we have a valid color for each asset class
          const color = ASSET_CLASS_COLORS[asset.name.toLowerCase() as keyof typeof ASSET_CLASS_COLORS] || "#808080"; // Fallback color
          return {
            ...acc,
            [asset.name]: asset.value,
            [`${asset.name}Color`]: color
          };
        }, {})
      }];

      xAxis.data.setAll(data);
      
      (assetClasses || []).forEach(asset => {
        // Get color with fallback
        const color = ASSET_CLASS_COLORS[asset.name.toLowerCase() as keyof typeof ASSET_CLASS_COLORS] || "#808080";
        
        const series = chart.series.push(
          am5xy.ColumnSeries.new(root, {
            name: asset.name,
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: asset.name,
            categoryXField: "category",
            stacked: true,
            tooltip: tooltip,
            fill: am5.color(color),
            stroke: am5.color(color)
          })
        );

        series.columns.template.setAll({
          tooltipText: "{name}: {valueY}%",
          tooltipY: 0,
          cornerRadiusTL: 3,
          cornerRadiusTR: 3,
          width: am5.percent(70)
        });

        series.data.setAll(data);
      });
    }

    return () => {
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
        seriesRef.current = null;
      }
    };
  }, [assetClasses]);

  return <div id="chartdiv" className="w-full h-full" />;
};

export default AssetAllocationChart;